import type { ISpeedControlParameterDto } from "@framework/types";

import { getLineColor } from "../../scheme/components/analytics/utils/line-colors";
import type { IChartData } from "../../scheme/components/analytics/interfaces";
import { matchesTemplateFilters } from "./data-marks";
import type { IAnalysisTemplateScalarMeasure } from "./analysis-template-settings";
import { isFrequencyRangeBoundsInvalid, measureBandMatchesFrequencyFilter } from "./template-filters";
import {
  buildMultiAnalysisTrendSeries,
  type IMultiAnalysisMeasureDataResponse,
  type IMultiAnalysisMeasureOption,
} from "./trend-series";
import { buildMeasureRowLabel, type ITemplateMeasureMetadata } from "./template-measure-rows";
import type { IStructureTreePoint } from "../types";

export const SCALAR_MEASURE_TYPES = [1, 2, 3] as const;

export const SCALAR_MEASURE_TYPE_OPTIONS = ["1", "2", "3"] as const;

export const KEYPHASOR_UNIT_OPTIONS = ["135", "140"] as const;

export const PARAMETER_UNIT_OPTIONS = KEYPHASOR_UNIT_OPTIONS;

const SERIES_LABEL_SEPARATOR = " / ";

export interface IScalarMeasureMetadata extends ITemplateMeasureMetadata {
  type: number | null;
}

export interface IScalarMeasureRow {
  key: string;
  name: string;
  pointNames: string[];
  metadata: {
    type: number | null;
    rangeType: number | null;
    units: number | null;
    presentation: number | null;
    param1: number | null;
    param2: number | null;
    param3: number | null;
  };
}

export interface IScalarLatestMeasureItem {
  idPoint: number;
  units?: unknown;
  rangeType?: unknown;
  presentation?: unknown;
  param1?: unknown;
  param2?: unknown;
  param3?: unknown;
  type?: unknown;
  Type?: unknown;
}

export interface IScalarLatestMeasureGroup {
  name: string;
  data: readonly IScalarLatestMeasureItem[];
}

const isScalarMeasureType = (type: number | null): boolean =>
  type !== null && SCALAR_MEASURE_TYPES.includes(type as (typeof SCALAR_MEASURE_TYPES)[number]);

const toNullableNumber = (value: unknown): number | null => {
  if (value === null || value === undefined || String(value).trim() === "") {
    return null;
  }

  const numericValue = Number(value);

  return Number.isFinite(numericValue) ? numericValue : null;
};

const metadataKey = (metadata: IScalarMeasureRow["metadata"]): string =>
  [
    metadata.type ?? "x",
    metadata.rangeType ?? "x",
    metadata.units ?? "x",
    metadata.presentation ?? "x",
    metadata.param1 ?? "x",
    metadata.param2 ?? "x",
    metadata.param3 ?? "x",
  ].join(":");

export const groupScalarMeasureRows = (measures: readonly IScalarMeasureMetadata[]): IScalarMeasureRow[] => {
  const grouped = new Map<string, IScalarMeasureRow>();

  measures.forEach(measure => {
    const metadata = {
      type: measure.type,
      rangeType: measure.rangeType,
      units: measure.units,
      presentation: measure.presentation,
      param1: measure.param1,
      param2: measure.param2,
      param3: measure.param3,
    };
    const key = `${measure.name}::${metadataKey(metadata)}`;
    const existing = grouped.get(key);

    if (existing) {
      if (!existing.pointNames.includes(measure.pointName)) {
        existing.pointNames.push(measure.pointName);
      }
      return;
    }

    grouped.set(key, {
      key,
      name: measure.name,
      pointNames: [measure.pointName],
      metadata,
    });
  });

  return Array.from(grouped.values()).sort((left, right) => left.name.localeCompare(right.name));
};

export const buildScalarMeasureRowLabel = (row: IScalarMeasureRow): string =>
  buildMeasureRowLabel({
    key: row.key,
    name: row.name,
    pointNames: row.pointNames,
    metadata: row.metadata,
  });

export const buildScalarMeasureMetadataFromOptions = (
  options: readonly Pick<IMultiAnalysisMeasureOption, "title" | "type" | "rangeType">[],
  points: readonly Pick<IStructureTreePoint, "id" | "title">[],
): IScalarMeasureMetadata[] =>
  options.flatMap(option => {
    if (!isScalarMeasureType(option.type) || !option.title) {
      return [];
    }

    return points.map(point => ({
      name: option.title,
      idPoint: point.id,
      pointName: point.title,
      type: option.type,
      rangeType: option.rangeType,
      units: null,
      presentation: null,
      param1: null,
      param2: null,
      param3: null,
    }));
  });

export const buildScalarMeasureMetadata = (
  options: readonly Pick<IMultiAnalysisMeasureOption, "title" | "type" | "rangeType">[],
  groups: readonly IScalarLatestMeasureGroup[],
  pointNameById: ReadonlyMap<number, string>,
  pointFallback: (idPoint: number) => string,
): IScalarMeasureMetadata[] => {
  const optionsByName = new Map<string, Array<Pick<IMultiAnalysisMeasureOption, "type" | "rangeType">>>();

  options.forEach(option => {
    if (!isScalarMeasureType(option.type)) {
      return;
    }

    const current = optionsByName.get(option.title) ?? [];
    const alreadyStored = current.some(item => item.type === option.type && item.rangeType === option.rangeType);

    if (!alreadyStored) {
      current.push({ type: option.type, rangeType: option.rangeType });
      optionsByName.set(option.title, current);
    }
  });

  const metadata: IScalarMeasureMetadata[] = [];

  groups.forEach(group => {
    if (!group.name || !Array.isArray(group.data)) {
      return;
    }

    const nameOptions = optionsByName.get(group.name) ?? [];

    if (!nameOptions.length) {
      return;
    }

    group.data.forEach(item => {
      const itemType = toNullableNumber(item.type ?? item.Type);
      const matchedOptions = nameOptions.filter(option => itemType === null || option.type === itemType);
      const resolvedOptions = matchedOptions.length > 0 ? matchedOptions : nameOptions;

      resolvedOptions.forEach(option => {
        const type = itemType ?? option.type;

        if (!isScalarMeasureType(type)) {
          return;
        }

        metadata.push({
          name: group.name,
          idPoint: item.idPoint,
          pointName: pointNameById.get(item.idPoint) ?? pointFallback(item.idPoint),
          type,
          rangeType: toNullableNumber(item.rangeType) ?? option.rangeType,
          units: toNullableNumber(item.units),
          presentation: toNullableNumber(item.presentation),
          param1: toNullableNumber(item.param1),
          param2: toNullableNumber(item.param2),
          param3: toNullableNumber(item.param3),
        });
      });
    });
  });

  return metadata;
};

export const scalarMeasureFromRow = (row: IScalarMeasureRow): IAnalysisTemplateScalarMeasure => ({
  type: row.metadata.type,
  rangeType: row.metadata.rangeType,
  units: row.metadata.units,
  presentation: row.metadata.presentation,
  param1: row.metadata.param1,
  param2: row.metadata.param2,
  param3: row.metadata.param3,
  name: row.name,
});

export const scalarMeasureMatchesRow = (measure: IAnalysisTemplateScalarMeasure, row: IScalarMeasureRow): boolean => {
  if (!measure.name || measure.name !== row.name) {
    return false;
  }

  return (
    measure.type === row.metadata.type &&
    measure.rangeType === row.metadata.rangeType &&
    measure.units === row.metadata.units &&
    measure.presentation === row.metadata.presentation &&
    measureBandMatchesFrequencyFilter(row.metadata.param1, row.metadata.param2, {
      param1: measure.param1,
      param2: measure.param2,
    }) &&
    measure.param3 === row.metadata.param3
  );
};

export const toggleScalarMeasureRow = (
  measures: readonly IAnalysisTemplateScalarMeasure[],
  row: IScalarMeasureRow,
  checked: boolean,
): IAnalysisTemplateScalarMeasure[] => {
  const manualMeasures = measures.filter(measure => !measure.name);
  const namedMeasures = measures.filter(measure => measure.name && !scalarMeasureMatchesRow(measure, row));

  if (!checked) {
    return [...manualMeasures, ...namedMeasures];
  }

  return [...manualMeasures, ...namedMeasures, scalarMeasureFromRow(row)];
};

export const createEmptyScalarMeasure = (): IAnalysisTemplateScalarMeasure => ({
  type: SCALAR_MEASURE_TYPES[0],
  rangeType: null,
  units: null,
  presentation: null,
  param1: null,
  param2: null,
  param3: null,
  name: null,
});

export const createScalarMeasureFilters = (measure: IAnalysisTemplateScalarMeasure): Record<string, unknown> => {
  const filters: Record<string, unknown> = {
    rangeType: measure.rangeType,
    units: measure.units,
    presentation: measure.presentation,
    param1: measure.param1,
    param2: measure.param2,
    param3: measure.param3,
  };

  if (measure.type !== null) {
    filters.Type = measure.type;
  }

  if (measure.name) {
    filters.Name = measure.name;
  }

  return filters;
};

export const scalarMeasureMatchesOption = (
  measure: IAnalysisTemplateScalarMeasure,
  option: Pick<IMultiAnalysisMeasureOption, "title" | "type" | "rangeType">,
): boolean => {
  if (!isScalarMeasureType(option.type)) {
    return false;
  }

  if (measure.type !== null && option.type !== measure.type) {
    return false;
  }

  if (measure.name && measure.name !== option.title) {
    return false;
  }

  if (measure.rangeType !== null && option.rangeType !== null && measure.rangeType !== option.rangeType) {
    return false;
  }

  return true;
};

export const filterMeasureResponseByAnyScalarMeasures = (
  response: IMultiAnalysisMeasureDataResponse | null | undefined,
  measures: readonly IAnalysisTemplateScalarMeasure[],
): IMultiAnalysisMeasureDataResponse => {
  if (!response || !measures.length) {
    return {};
  }

  const filters = measures.map(createScalarMeasureFilters);

  return Object.fromEntries(
    Object.entries(response).flatMap(([key, measure]) => {
      if (!measure || !Array.isArray(measure.data)) {
        return [];
      }

      const data = measure.data.filter(row =>
        filters.some(filter => matchesTemplateFilters(row as unknown as Record<string, unknown>, filter)),
      );

      if (!data.length) {
        return [];
      }

      return [[key, { ...measure, data }]];
    }),
  );
};

export const scalarMeasuresHaveInvalidFrequencyRanges = (
  measures: readonly IAnalysisTemplateScalarMeasure[],
): boolean =>
  measures.some(measure =>
    isFrequencyRangeBoundsInvalid(
      measure.param1 === null ? "" : String(measure.param1),
      measure.param2 === null ? "" : String(measure.param2),
    ),
  );

export interface IParameterUnitOption {
  value: string;
  kind: ISpeedControlParameterDto["kind"];
}

export const resolveSpeedControlParameterUnitCode = (
  parameter: Pick<ISpeedControlParameterDto, "kind" | "units" | "unitLabel">,
): number | null => {
  if (parameter.kind === "keyphasor") {
    return parameter.units !== null && Number.isFinite(parameter.units) ? parameter.units : null;
  }

  const label = parameter.unitLabel?.trim() ?? "";

  if (!label) {
    return null;
  }

  const numericValue = Number(label);

  return Number.isFinite(numericValue) ? numericValue : null;
};

export const resolveSpeedControlParameterUnitValue = (
  parameter: Pick<ISpeedControlParameterDto, "kind" | "units" | "unitLabel">,
): string | null => {
  if (parameter.kind === "keyphasor") {
    const code = resolveSpeedControlParameterUnitCode(parameter);

    return code !== null ? String(code) : null;
  }

  const label = parameter.unitLabel?.trim() ?? "";

  return label || null;
};

export const collectParameterUnitOptions = (
  parameters: readonly Pick<ISpeedControlParameterDto, "kind" | "units" | "unitLabel">[],
): IParameterUnitOption[] => {
  const options = new Map<string, IParameterUnitOption>();

  const ordered = [
    ...parameters.filter(parameter => parameter.kind === "keyphasor"),
    ...parameters.filter(parameter => parameter.kind !== "keyphasor"),
  ];

  ordered.forEach(parameter => {
    const value = resolveSpeedControlParameterUnitValue(parameter);

    if (value === null || options.has(value)) {
      return;
    }

    options.set(value, { value, kind: parameter.kind });
  });

  return Array.from(options.values()).sort((left, right) => {
    if (left.kind !== right.kind) {
      return left.kind === "keyphasor" ? -1 : 1;
    }

    return left.value.localeCompare(right.value, undefined, { numeric: true });
  });
};

export const filterParametersByUnitCodes = <T extends Pick<ISpeedControlParameterDto, "kind" | "units" | "unitLabel">>(
  parameters: readonly T[],
  unitCodes: readonly number[] | readonly string[],
): T[] => {
  if (!unitCodes.length) {
    return [];
  }

  const selected = new Set(unitCodes.map(String));

  return parameters.filter(parameter => {
    const value = resolveSpeedControlParameterUnitValue(parameter);

    return value !== null && selected.has(value);
  });
};

export const nextParameterUnit = (selectedUnits: readonly number[] | readonly string[]): string => {
  const selected = new Set(selectedUnits.map(String));
  const available = PARAMETER_UNIT_OPTIONS.find(unit => !selected.has(unit));

  return available ?? PARAMETER_UNIT_OPTIONS[0];
};

export interface IParameterTrendRow {
  Date?: string;
  date?: string;
  Value?: number;
  value?: number;
}

export const buildParameterTrendSeries = (
  parameters: readonly ISpeedControlParameterDto[],
  rowsByDataId: ReadonlyMap<number, readonly IParameterTrendRow[]>,
  options: {
    trainId: number;
    unitLabelFormatter: (unitCode: number) => string;
    kindLabelFormatter: (kind: ISpeedControlParameterDto["kind"]) => string;
    colorOffset?: number;
  },
): IChartData[] =>
  parameters.flatMap((parameter, index) => {
    const rows = rowsByDataId.get(parameter.dataId) ?? [];
    const items = rows.flatMap(row => {
      const date = row.Date ?? row.date;
      const value = row.Value ?? row.value;

      if (!date || !Number.isFinite(Number(value))) {
        return [];
      }

      return [{ date, value: Number(value) }];
    });

    if (!items.length) {
      return [];
    }

    const unitCode = resolveSpeedControlParameterUnitCode(parameter);
    const kindLabel = options.kindLabelFormatter(parameter.kind);

    return [
      {
        idTrain: options.trainId,
        paramId: parameter.dataId,
        name: `${kindLabel}${SERIES_LABEL_SEPARATOR}${parameter.title}`,
        parameterName: parameter.title,
        color: getLineColor(index + (options.colorOffset ?? 0)),
        units: unitCode !== null ? options.unitLabelFormatter(unitCode) : parameter.unitLabel ?? "",
        unitCode,
        items,
      },
    ];
  });

export const buildScalarTrendSeries = (
  response: IMultiAnalysisMeasureDataResponse | null | undefined,
  points: readonly IStructureTreePoint[],
  measureOptions: readonly IMultiAnalysisMeasureOption[],
  measures: readonly IAnalysisTemplateScalarMeasure[],
  options: {
    unitLabelFormatter?: (unitCode: number) => string;
    pointDirectionLabelFormatter?: (direction: number) => string;
  } = {},
): IChartData[] =>
  buildMultiAnalysisTrendSeries(
    filterMeasureResponseByAnyScalarMeasures(response, measures),
    points,
    measureOptions,
    options,
  );
